import React, {useEffect, useRef, useState} from "react";
import Video from "twilio-video";
import {StompSessionProvider, useStompClient, useSubscription} from "react-stomp-hooks";
import {StompHeaders} from "@stomp/stompjs";
import imageService from "../../../service/imageService";
import {GeoCoords} from "../../../dto/geoCoords";
import {useKeycloak} from "@react-keycloak/web";
import {UploadStatus} from "../../../enums/UploadStatus";
import {notification} from "antd";
import ErrorHandler from "../../../util/ErrorHandler";

const baseUrl: string | undefined = process.env.BASE_API_URL ? process.env.BASE_API_URL : "";
const LocalParticipantView: React.FC<{
    participant: Video.LocalParticipant,
    username: string,
    remoteUserAccessToken: string,
    geoCoords: GeoCoords,
    facingMode: string,
    notifyMessage(message: string): any
    canShowRecording(value: boolean): any
}> = ({
          participant,
          username,
          remoteUserAccessToken,
          geoCoords,
          facingMode,
          notifyMessage,
          canShowRecording
      }) => {

    const {keycloak} = useKeycloak();

    const [videoTracks, setVideoTracks] = useState<any>([]);
    const [audioTracks, setAudioTracks] = useState<any>([]);
    const [playVideoTrack, setPlayVideoTrack] = useState<any>();
    const [cameraTrack, setCameraTrack] = useState<any>();


    const videoRef: any = useRef();
    const audioRef: any = useRef();
    const canvasRef: any = useRef();

    let width = 320;
    let height = 320;
    const imageWidth = 60;
    let imageHeight = 60;
    let localVideo: any;

    const trackPubsToTracks = (trackMap: any) =>
        Array.from(trackMap.values())
            .map((publication: any) => publication.track)
            .filter((track) => track !== null);

    useEffect(() => {
        setVideoTracks(trackPubsToTracks(participant.videoTracks));
        setAudioTracks(trackPubsToTracks(participant.audioTracks));

        const trackSubscribed = (track: any) => {
            if (track.kind === "video") {
                setVideoTracks((videoTracks: any) => [...videoTracks, track]);

            } else if (track.kind === "audio") {
                setAudioTracks((audioTracks: any) => [...audioTracks, track]);
            }
        };

        const trackUnsubscribed = (track: any) => {
            if (track.kind === "video") {
                setVideoTracks((videoTracks: any) => videoTracks.filter((v: any) => v !== track));
            } else if (track.kind === "audio") {
                setAudioTracks((audioTracks: any) => audioTracks.filter((a: any) => a !== track));
            }
        };

        participant.on("trackSubscribed", trackSubscribed);
        participant.on("trackUnsubscribed", trackUnsubscribed);

        return () => {
            setVideoTracks([]);
            setAudioTracks([]);
            //   setCameraTrack(null);
            participant.removeAllListeners();
        };
    }, [participant]);

    useEffect(() => {
        const videoTrack = videoTracks[0];
        if (videoTrack) {
            videoTrack.attach(videoRef.current);
            setPlayVideoTrack(videoRef.current);
            setCameraTrack(videoTrack);
            videoRef.current.play();
            return () => {
                videoTrack.detach();
            };
        }
    }, [videoTracks]);

    useEffect(() => {
        const audioTrack = audioTracks[0];
        if (audioTrack) {
            audioTrack.attach(audioRef.current);
            return () => {
                audioTrack.detach();
            };
        }
    }, [audioTracks]);


    const takePicture = async (imageType: string, documentTypeId: number): Promise<string> => {

        let canvas = canvasRef.current;
        localVideo = playVideoTrack;
        height = localVideo.videoHeight;
        width = localVideo.videoWidth;
        imageHeight = localVideo.videoHeight / (localVideo.videoWidth / imageWidth);
        if (isNaN(imageHeight)) {
            imageHeight = imageWidth / (4 / 3);
        }
        canvas.setAttribute('width', width);
        canvas.setAttribute('height', height);
        const context = canvasRef.current.getContext('2d');
        canvas.width = width;
        canvas.height = height;
        context.drawImage(localVideo, 0, 0, width, height);
        let data = canvas.toDataURL('image/png');
        let imageData = data.replace(/^data:image\/(png|jpg);base64,/, "");
        console.log("========== Uploading Image ===========")
        imageService.uploadImage({
            imageData: imageData,
            imageType: imageType,
            latitude: geoCoords.latitude,
            longitude: geoCoords.longitude,
            documentTypeId: documentTypeId,
            remoteUserAccessToken: remoteUserAccessToken
        }).then(value => {
            //Open notification window
            console.log(value)
        }).catch(reason => {
            ErrorHandler.handleError(reason)
        });
        return UploadStatus.UPLOADED;
    }

    useEffect(() => {
        const switchCamara = () => {
            console.log('child function 1 called', facingMode, cameraTrack);
            if (facingMode === "environment") {
                cameraTrack?.restart({facingMode: 'user'});
            } else {
                cameraTrack?.restart({facingMode: 'environment'});
            }
        };


        // 👇️ don't run on initial render
        if (facingMode !== "") {
            switchCamara();
        }
    }, [facingMode]);

    return (
        <StompSessionProvider connectHeaders={{
            "accessToken": keycloak.token ? keycloak.token : "",
            "username": username
        }}
                              onConnect={receipt => console.log("receipt : ", receipt.headers)}
                              url={`${baseUrl}/video/socket`}
                              onWebSocketClose={(evt) => {
                                  console.log('WebSocket closed: ', evt);
                              }}
        >
            <>
                <SubscribingComponent username={username} onTakePicture={takePicture} setNotifyMessage={notifyMessage} setCanShowRecording={canShowRecording} />
                <canvas ref={canvasRef} style={{width: '100%', height: '100%', display: "none"}}></canvas>
                <div className="participant">
                    {/*<h3>{participant.identity}</h3>*/}
                    <video className='videoFrame' ref={videoRef} autoPlay={true} muted={true} playsInline={true}/>
                    <audio ref={audioRef} autoPlay={true} muted={true}/>
                </div>
            </>

        </StompSessionProvider>
    );
};

const key = 'updatable';
const SubscribingComponent: React.FC<{
    username: string,
    onTakePicture(imageType: string, documentTypeId: number): any
    setNotifyMessage(message: string): any
    setCanShowRecording(value: boolean): any
}> = ({
          username,
          onTakePicture,
          setNotifyMessage,
          setCanShowRecording
      }) => {
    // const [message, setMessage] = useState<string>("");
    const [api] = notification.useNotification();
    const {keycloak} = useKeycloak();

    let stompHeaders: StompHeaders = {
        "accessToken": keycloak.token ? keycloak.token : "",
        "username": username
    }

    const stompClient = useStompClient();
    // const stompClient = useStompClient();


    useSubscription("/user/topic/received-image-upload-request", (message) => {

        let res: any = JSON.parse(message.body);

        stompClient?.publish({
            destination: `/video/app/acknowledge`,
            body: message.headers.messageId
        });
        console.log(`received-image-upload-request Acknowledgment sent for message: ${message.headers.messageId}`);

        console.log("IMAGE : ", res)
        if (res.content, res.errorMessage) {
            console.log("========= error ===================")
        } else {
            setNotifyMessage("Please wait!!.. Image capture is processing");
            // api.info({
            //     key,
            //     message: 'Image Capturing',
            //     description: 'Please wait!!.. Image capture is processing',
            //     placement: "top",
            //     duration: 0
            // });
            console.log("========= Taking Picture ===================")
            onTakePicture(res.imageType, res.documentTypeId).then((val: any) => {

            });

        }
    }, stompHeaders);

    useSubscription("/user/topic/remote-recording_start", (message) => {
        if (message.body) {
            console.log("=========== Video Recording Started ================")
            setNotifyMessage("Video Recording Started")
            setCanShowRecording(true)
            api.destroy(key);
        } else {
            console.log("=========== Failed to Start Video Recoding ================")
            setNotifyMessage("Failed to Start Video Recoding")
            setCanShowRecording(false)
        }
    }, stompHeaders);

    useSubscription("/user/topic/remote-recording_stop", (message) => {
        if (message.body) {
            console.log("=========== Video Recording Stopped ================")
            setNotifyMessage("Video Recording Stopped")
            setCanShowRecording(false)
            api.destroy(key);
        } else {
            console.log("=========== Failed to Stop Video Recoding ================")
            setNotifyMessage("Failed to Stop Video Recoding")
            setCanShowRecording(false)
        }
    }, stompHeaders);

    useSubscription("/user/topic/remote-check-image-upload-response", (message) => {
        let res: any = JSON.parse(message.body);

        stompClient?.publish({
            destination: `/video/app/acknowledge`,
            body: message.headers.messageId
        });
        console.log(`remote-check-image-upload-response Acknowledgment sent for message: ${message.headers.messageId}`);

        console.log("IMAGE : ", res)
        if (res.content, res.errorMessage) {
            // alert("error")
            console.log("=========== Image Capturing Failed ================")
            setNotifyMessage("Image Capturing Failed!")
        } else {
            /*api.success({
                key,
                message: 'Image Capturing...',
                description: 'Please wait!!.. Image capture is processing',
                placement: "top",
                duration: 0
            });*/
            // setMessage("Image Captured");
            console.log("=========== Image Captured ================")
            setNotifyMessage("Image Captured")
            api.destroy(key);
        }
    }, stompHeaders);

    return <>
        {/*{contextHolder}*/}
        {/*<div>{message}</div>*/}
    </>
}

export default LocalParticipantView;
